<template>
  <div class="wrapper">
    <input
      type="file"
      ref="file"
      class="input"
      @change="onChange"
      :name="inputName"
      :id="inputName"
      :disabled="disabled"
      :accept="acceptInput"
      multiple
    />
    <label class="label" :for="inputName"></label>
    <slot><span class="default-message">Click to Download</span></slot>
    <div class="drop-message" v-if="isDropOver">
      <div class="drop-message-text">
        <slot name="drop-message">Drop File Here</slot>
      </div>
    </div>
  </div>
</template>

<script>
import Id from '@/utils/Id'
import { formatBytes } from '@/utils/Helpers'

export default {
  name: 'AppFilesSelector',
  props: {
    acceptInput: {
      type: String,
      default: '*/*'
    },
    mimeFilter: {
      type: String,
      default: '*/*'
    },
    dropArea: {
      type: Element,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    elements: [],
    inputName: Id(),
    isDropOver: false
  }),
  methods: {
    filterFilesByMime(files) {
      if (this.mimeFilter === '*/*') {
        return files
      }

      const validMimeTypes = this.mimeFilter.split(',')
      return files.filter((file) => {
        return validMimeTypes.some((type) => {
          if (file.type === type) {
            return true
          }

          const asteriskPosition = type.indexOf('/*')
          return asteriskPosition !== -1 && file.type.indexOf(type.substring(0, asteriskPosition)) !== -1
        })
      })
    },
    onChange(e) {
      if (this.disabled) return
      if (e.target.files && e.target.files.length) {
        let files = this.filterFilesByMime([...e.target.files])
        if (files.length === 0) {
          this.emitError('Selected file types not allowed!')
          return
        }

        if (this.maxSize > 0) {
          const filesInMaxLimit = files.every((v) => v.size <= this.maxSize)
          if (!filesInMaxLimit) {
            // this.emitError(`Some of selected files is too big! Allowed size: ${formatBytes(this.maxSize)}`)
            this.emitError(`The Icon image is too large. Please upload an Icon image smaller than ${formatBytes(this.maxSize)}`)
            return
          }
        }

        this.$emit('change', files)
        e.target.value = ''
      }
    },
    emitError(message) {
      this.$emit('error', message)
    },
    onDrop(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDropOver = false
      if (e.dataTransfer && e.dataTransfer.items) {
        let files = []
        let len = e.dataTransfer.items.length
        for (let i = 0; i < len; i++) {
          let item = e.dataTransfer.items[i]
          if (item.kind === 'file') {
            files.push(item.getAsFile())
          }
        }

        this.$emit('change', this.filterFilesByMime(files))
      }
    },
    onDragOver(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    onDragLeave(e) {
      e.preventDefault()
      e.stopPropagation()
      if (!this.dropArea.contains(e.target)) {
        this.isDropOver = false
      }
    },
    onDragEnter(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDropOver = true
    },
    dropAreaBind(element) {
      element.addEventListener('dragenter', this.onDragEnter)
      element.addEventListener('dragover', this.onDragOver)
      element.addEventListener('dragleave', this.onDragLeave)
      element.addEventListener('drop', this.onDrop)
    },
    dropAreaUnbind(element) {
      element.removeEventListener('dragenter', this.onDragEnter)
      element.removeEventListener('dragover', this.onDragOver)
      element.removeEventListener('dragleave', this.onDragLeave)
      element.removeEventListener('drop', this.onDrop)
    }
  },
  beforeDestroy() {
    this.dropAreaUnbind(this.dropArea)
  },
  watch: {
    dropArea: {
      immediate: true,
      handler(element, oldElement) {
        if (element) {
          this.dropAreaBind(element)
        }

        if (oldElement) {
          this.dropAreaUnbind(oldElement)
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.wrapper {
  display: inline-block;
  position: relative;
  overflow: hidden;

  .input {
    background: rgba(255, 255, 255, 0);
    overflow: hidden;
    position: fixed;
    width: 1px;
    height: 1px;
    z-index: -1;
    opacity: 0;
  }

  .label {
    cursor: pointer;
    background: #fff;
    opacity: 0;
    font-size: 20em;
    z-index: 3;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .default-message {
    font-size: 12px;
    color: #218bc3;
  }

  .drop-message {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    position: fixed;
    z-index: 9999;
    opacity: 0.6;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;

    .drop-message-text {
      color: #fff;
      font-size: 34px;
      line-height: 44px;
    }
  }
}
</style>
